import { useControllableState } from '@anchorage/hooks';
import { useEffect, useMemo, useState } from 'react';
import type { DateRange } from 'react-day-picker';
import { useInput } from 'react-day-picker';
import type { DatePickerMode, DatePickerValue, DatepickerProps } from 'types';
import {
  getDefaultMaxDate,
  getDefaultMinDate,
  getDefaultValue,
  isValidDate,
} from 'utils/dateUtils';

import type { DatepickerProviderProps } from './HookTypes';

type UseDatepickerStateProps = {
  mode: DatepickerProviderProps['mode'];
  controlledOpen?: DatepickerProviderProps['controlledOpen'];
  controlledSetOpen?: DatepickerProviderProps['controlledSetOpen'];
  controlledValue?: DatepickerProviderProps['value'];
  required?: DatepickerProviderProps['required'];
  format?: Exclude<DatepickerProps['format'], undefined>;
  controlledMinDate?: Exclude<DatepickerProps['minDate'], undefined>;
  controlledMaxDate?: Exclude<DatepickerProps['maxDate'], undefined>;
};

const sanitizeDate = (
  mode: string,
  date: unknown,
): DatePickerValue<DatePickerMode> | undefined => {
  if (mode === 'range') {
    if (date && typeof date === 'object' && 'from' in date && 'to' in date) {
      return {
        from: isValidDate(date.from) ? date.from : undefined,
        to: isValidDate(date.to) ? date.to : undefined,
      };
    }
  } else {
    // should only return the date prop if the payload is of type Date or DateRange
    if (isValidDate(date)) {
      return date;
    }
  }
  return undefined;
};

// Compare dates to avoid infinite loops from unnecessary state updates
const areDatesEqual = (a: Date | undefined, b: Date | undefined): boolean => {
  if (a === b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  return a.getTime() === b.getTime();
};

const isValueEqual = (
  mode: DatePickerMode,
  current: DatePickerValue<typeof mode> | undefined,
  next: DatePickerValue<typeof mode> | undefined,
): boolean => {
  if (current === next) {
    return true;
  }
  if (!current || !next) {
    return current === next;
  }

  if (mode === 'range') {
    const currentRange = current as DateRange;
    const nextRange = next as DateRange;
    return (
      areDatesEqual(currentRange.from, nextRange.from) &&
      areDatesEqual(currentRange.to, nextRange.to)
    );
  }

  return areDatesEqual(current as Date, next as Date);
};

export const useDatepickerState = ({
  mode,
  controlledOpen,
  controlledSetOpen,
  controlledValue,
  required,
  format,
  controlledMinDate,
  controlledMaxDate,
}: UseDatepickerStateProps) => {
  const [open, setOpen] = useControllableState(
    false,
    controlledOpen,
    controlledSetOpen,
  );

  const sanitizedDate = useMemo(
    () => sanitizeDate(mode, controlledValue),
    [mode, controlledValue],
  );

  const [exportedValue, setExportedValue] = useState<
    DatePickerValue<typeof mode> | undefined
  >(undefined);

  const [tempValue, setTempValue] = useState<
    DatePickerValue<typeof mode> | undefined
  >(undefined);

  const isRangeAndFromAlreadySet = Boolean(
    mode === 'range' && (sanitizedDate as DateRange)?.from !== undefined,
  );

  const [rangeDirection, setRangeDirection] = useState<'from' | 'to'>(
    isRangeAndFromAlreadySet ? 'to' : 'from',
  );
  const [changingMonth, setChangingMonth] = useState(false);
  const [changingYear, setChangingYear] = useState(false);
  const [yearPage, setYearPage] = useState(0);

  const minDate = useMemo(
    () => getDefaultMinDate(controlledMinDate),
    [controlledMinDate],
  );
  const maxDate = useMemo(
    () => getDefaultMaxDate(controlledMaxDate),
    [controlledMaxDate],
  );

  // Initialize input states for 'from' and 'to' values
  const fromDate = useInput({
    defaultSelected: getDefaultValue('from', sanitizedDate),
    fromDate: minDate,
    toDate: maxDate,
    format,
    required, // Will be updated based on props
  });
  const toDate = useInput({
    defaultSelected: getDefaultValue('to', sanitizedDate),
    fromDate: minDate,
    toDate: maxDate,
    format,
    required, // Will be updated based on props
  });
  const [calendarFromDateSelected, setCalendarFromDateSelected] = useState<
    Date | undefined
  >(undefined);
  const [calendarToDateSelected, setCalendarToDateSelected] = useState<
    Date | undefined
  >(undefined);

  useEffect(() => {
    const newFromDate = getDefaultValue('from', sanitizedDate);
    const newToDate = getDefaultValue('to', sanitizedDate);

    // Only update if values have actually changed
    if (!isValueEqual(mode, exportedValue, sanitizedDate)) {
      setExportedValue(sanitizedDate);
    }
    if (!isValueEqual(mode, tempValue, sanitizedDate)) {
      setTempValue(sanitizedDate);
    }
    if (!areDatesEqual(calendarFromDateSelected, newFromDate)) {
      setCalendarFromDateSelected(newFromDate);
    }
    if (!areDatesEqual(calendarToDateSelected, newToDate)) {
      setCalendarToDateSelected(newToDate);
    }
    // When the controlled value is cleared externally, reset range direction so
    // the next interaction starts from the 'from' pick rather than getting stuck
    // in the 'to' state with no from-date set (which silently swallows clicks).
    if (!sanitizedDate && mode === 'range') {
      setRangeDirection('from');
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [sanitizedDate, mode]);

  const returnValue = useMemo(
    () => ({
      open,
      setOpen,
      changingMonth,
      setChangingMonth,
      changingYear,
      setChangingYear,
      yearPage,
      setYearPage,
      tempValue,
      setTempValue,
      rangeDirection,
      setRangeDirection,
      exportedValue,
      setExportedValue,
      calendarFromDateSelected,
      calendarOnFromDateChange: fromDate.setSelected,
      calendarToDateSelected,
      calendarOnToDateChange: toDate.setSelected,
      fromDateInputProps: fromDate.inputProps,
      toDateInputProps: toDate.inputProps,
      minDate,
      maxDate,
    }),
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [
      open,
      changingMonth,
      changingYear,
      yearPage,
      tempValue,
      rangeDirection,
      exportedValue,
      calendarFromDateSelected,
      fromDate,
      toDate,
      calendarToDateSelected,
      minDate,
      maxDate,
    ],
  );

  return returnValue;
};
